import { memo, useMemo } from "react";
import PropTypes from "prop-types";

/**
 * GaugeWidget – Detached floating SCADA gauge/dial visualization.
 *
 * Renders as a standalone floating widget on the canvas, connected to
 * its parent node via a dashed connector line (WidgetConnector edge).
 *
 * Features:
 * - Semicircular gauge with needle
 * - Color zones (normal, warning, critical)
 * - Min/max labels + current value display
 * - Compact industrial appearance
 * - Glassmorphism container styling
 */
const GaugeWidget = memo(({ value = 0, min = 0, max = 100, unit = "", color = "#3b82f6", label = "", width, height, greenStart = null, greenEnd = null, yellowStart = null, yellowEnd = null, redStart = null, redEnd = null }) => {
  const size = Math.min(width || 140, (height || 140) * 1.5);
  const clamped = Math.min(max, Math.max(min, Number(value) || 0));
  const pct = max > min ? (clamped - min) / (max - min) : 0;

  // Semicircle arc params
  const cx = size / 2;
  const cy = size / 2 + 8;
  const r = Math.max(1, size / 2 - 14);
  const startAngle = Math.PI;
  const needleAngle = startAngle - pct * Math.PI;

  // Arc path for the gauge track
  const arcPath = (start, end) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    const largeArc = Math.abs(start - end) > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // Color zones
  const getZoneColor = useMemo(() => {
    const v = Number(value) || 0;
    if (greenStart !== null && greenEnd !== null && v >= Number(greenStart) && v <= Number(greenEnd)) {
      return "#10b981"; // green
    }
    if (yellowStart !== null && yellowEnd !== null && v >= Number(yellowStart) && v <= Number(yellowEnd)) {
      return "#f59e0b"; // yellow
    }
    if (redStart !== null && redEnd !== null && v >= Number(redStart) && v <= Number(redEnd)) {
      return "#ef4444"; // red
    }
    if (pct > 0.85) return "#ef4444";
    if (pct > 0.65) return "#f59e0b";
    return color;
  }, [value, pct, color, greenStart, greenEnd, yellowStart, yellowEnd, redStart, redEnd]);

  // Needle endpoint
  const needleX = cx + (r - 6) * Math.cos(needleAngle);
  const needleY = cy - (r - 6) * Math.sin(needleAngle);

  // Tick marks
  const ticks = useMemo(() => {
    const marks = [];
    for (let i = 0; i <= 10; i++) {
      const angle = Math.PI - (i / 10) * Math.PI;
      const isMajor = i % 5 === 0;
      const outerR = r + 4;
      const innerR = isMajor ? r - 6 : r - 3;
      marks.push({
        x1: cx + outerR * Math.cos(angle),
        y1: cy - outerR * Math.sin(angle),
        x2: cx + innerR * Math.cos(angle),
        y2: cy - innerR * Math.sin(angle),
        isMajor,
      });
    }
    return marks;
  }, [cx, cy, r]);

  return (
    <div className="scada-widget scada-widget--gauge">
      {/* Widget header */}
      {label && <div className="scada-widget__label" style={{ fontSize: size * 0.1, marginBottom: 4 }}>{label}</div>}

      <svg width={size} height={size / 2 + 50} viewBox={`0 0 ${size} ${size / 2 + 34}`} style={{ overflow: "visible" }} fill="none">
        {/* Tick marks */}
        {ticks.map((t, i) => (
          <line
            key={i}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="#94a3b8"
            strokeWidth={t.isMajor ? 1.5 : 0.8}
            opacity={t.isMajor ? 0.6 : 0.3}
          />
        ))}

        {/* Background track */}
        <path
          d={arcPath(startAngle, 0)}
          stroke="#e2e8f0"
          strokeWidth={size * 0.06}
          strokeLinecap="round"
          fill="none"
        />

        {/* Filled arc */}
        <path
          d={arcPath(startAngle, needleAngle)}
          stroke={getZoneColor}
          strokeWidth={size * 0.06}
          strokeLinecap="round"
          fill="none"
          style={{ transition: "d 0.5s ease-out" }}
        />

        {/* Needle */}
        <line
          x1={cx} y1={cy} x2={needleX} y2={needleY}
          stroke={getZoneColor}
          strokeWidth={size * 0.02}
          strokeLinecap="round"
          style={{ transition: "x2 0.5s ease-out, y2 0.5s ease-out" }}
        />
        <circle cx={cx} cy={cy} r={size * 0.035} fill={getZoneColor} />
        <circle cx={cx} cy={cy} r={size * 0.02} fill="#fff" />

        {/* Value */}
        <text
          x={cx} y={cy + (size * 0.14)}
          textAnchor="middle"
          fontSize={size * 0.13}
          fontWeight="700"
          fill={getZoneColor}
          fontFamily="'Inter', 'Segoe UI', sans-serif"
        >
          {Number(clamped).toFixed(1)}
        </text>

        {/* Unit */}
        {unit && (
          <text
            x={cx} y={cy + (size * 0.23)}
            textAnchor="middle"
            fontSize={size * 0.09}
            fill="#94a3b8"
            fontFamily="'Inter', 'Segoe UI', sans-serif"
          >
            {unit}
          </text>
        )}

        {/* Min/Max labels */}
        <text x="10" y={cy + 13} fontSize={size * 0.08} fill="#94a3b8" fontFamily="'Inter', sans-serif">{min}</text>
        <text x={size - 10} y={cy + 13} textAnchor="end" fontSize={size * 0.08} fill="#94a3b8" fontFamily="'Inter', sans-serif">{max}</text>
      </svg>
    </div>
  );
});

GaugeWidget.displayName = "GaugeWidget";

GaugeWidget.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  unit: PropTypes.string,
  color: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  greenStart: PropTypes.number,
  greenEnd: PropTypes.number,
  yellowStart: PropTypes.number,
  yellowEnd: PropTypes.number,
  redStart: PropTypes.number,
  redEnd: PropTypes.number,
};

export default GaugeWidget;
